import { IntegrationConnection } from '@aibees/elephant/types/integration';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { IntegrationCampaignOption } from '../../integration-select-campaign';
import { IntegrationExportSettingItem } from '../components/integration-export-setting';

interface IntegrationContextProps {
  selectedIntegration?: IntegrationConnection;
  setSelectedIntegration: (selectedIntegration?: IntegrationConnection) => void;
  selectedCampaign?: IntegrationCampaignOption;
  setSelectedCampaign: (selectedCampaign?: IntegrationCampaignOption) => void;
  integration?: IntegrationConnection[];
  setIntegration: (integration?: IntegrationConnection[]) => void;
  integrationExportSetting?: IntegrationExportSettingItem;
  setIntegrationExportSetting: (
    integrationExportSetting?: IntegrationExportSettingItem
  ) => void;
}

const IntegrationContext = createContext<IntegrationContextProps | undefined>(
  undefined
);

export const useIntegrationContext = () => {
  const context = useContext(IntegrationContext);
  if (!context) {
    throw new Error(
      'useIntegrationContext must be used within a IntegrationProvider'
    );
  }
  return context;
};

export const IntegrationProvider = ({ children }: { children: ReactNode }) => {
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationConnection>();
  const [integration, setIntegration] = useState<IntegrationConnection[]>();
  const [selectedCampaign, setSelectedCampaign] =
    useState<IntegrationCampaignOption>();
  const [integrationExportSetting, setIntegrationExportSetting] =
    useState<IntegrationExportSettingItem>();

  const memoizedContextValue = useMemo(
    () => ({
      selectedCampaign,
      setSelectedCampaign,
      selectedIntegration,
      setSelectedIntegration,
      setIntegration,
      integration,
      setIntegrationExportSetting,
      integrationExportSetting,
    }),
    [
      selectedCampaign,
      selectedIntegration,
      integration,
      integrationExportSetting,
    ]
  );

  return (
    <IntegrationContext.Provider value={memoizedContextValue}>
      {children}
    </IntegrationContext.Provider>
  );
};
